import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useLocalMode } from '../../hooks/useLocalMode';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'admin' | 'superadmin';
  requiresLocal?: boolean;
  requiresSubscription?: boolean;
}

export function ProtectedRoute({
  children,
  requiredRole,
  requiresLocal = false,
  requiresSubscription = true,
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, hasRole, license, openLogin } = useAuth();
  const { isLocalAvailable } = useLocalMode();

  // Auto-open login popup when not authenticated
  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      openLogin({
        redirectOnCancel: '/',
        title: 'Authentication Required',
        description: 'Please sign in to access this page',
      });
    }
  }, [isAuthenticated, isLoading, openLogin]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        <div className="text-muted-foreground mt-4">Loading...</div>
      </div>
    );
  }

  // Check authentication - show loading/login state
  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="bg-card p-6 rounded-lg max-w-md text-center">
          <h2 className="text-xl font-semibold mb-2">Authentication Required</h2>
          <p className="text-muted-foreground mb-4">
            Please sign in to access this page. A login dialog should open automatically.
          </p>
          <button
            type="button"
            onClick={() =>
              openLogin({
                redirectOnCancel: '/',
                title: 'Authentication Required',
                description: 'Please sign in to access this page',
              })
            }
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  // Check subscription
  if (requiresSubscription && license && new Date(license.validUntil) < new Date()) {
    return <Navigate to="/account" state={{ needsSubscription: true }} replace />;
  }

  // Check role
  if (requiredRole && !hasRole(requiredRole)) {
    return <Navigate to="/ai" replace />;
  }

  // Check local mode requirement
  if (requiresLocal && !isLocalAvailable) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="bg-card p-6 rounded-lg max-w-md text-center">
          <h2 className="text-xl font-semibold mb-2">Local Mode Required</h2>
          <p className="text-muted-foreground">
            This feature requires a local LMM server. Please ensure LMM is running and try again.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
